import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, FileText, TrendingUp, Smartphone, ArrowRight, CheckCircle2 } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import './ServicesDetailed.css';

const ServicesDetailed = () => {
  return (
    <div className="services-detailed-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="services-hero-content">
          <h1 className="services-hero-title">Our Services</h1>
          <p className="services-hero-subtitle">
            Comprehensive consulting solutions designed to help your business succeed in the UAE
          </p>
        </div>
      </section>

      {/* Services Content */}
      <section className="services-content-section">
        <div className="services-container">
          
          {/* Accounting & Bookkeeping */}
          <div className="service-main-card">
            <div className="service-main-header">
              <div className="service-main-icon">
                <Calculator size={48} />
              </div>
              <div>
                <h2 className="service-main-title">Accounting & Bookkeeping Services</h2>
                <p className="service-main-subtitle">Professional Accounting and Bookkeeping Support for Growing Businesses</p>
              </div>
            </div>
            <img 
              src="https://images.pexels.com/photos/6962993/pexels-photo-6962993.jpeg" 
              alt="Accounting & Bookkeeping"
              className="service-main-image"
            />
            <div className="service-main-content">
              <p className="service-intro-text">
                At Fabsome Consulting, we provide reliable and structured accounting and bookkeeping services to businesses across Dubai and the wider UAE. Our services are designed to support startups, SMEs, and growing enterprises by ensuring accurate financial records, regulatory compliance, and meaningful financial insights.
              </p>
              
              <h3 className="service-section-title">Importance of Proper Accounting and Bookkeeping in the UAE</h3>
              <p className="service-text">
                Under UAE regulations, companies are required to maintain proper books of account for a minimum period, and many free zones mandate maintenance of accounts and annual audits by approved professionals. With increasing regulatory requirements such as VAT, Corporate Tax, and economic substance considerations, maintaining accurate and audit-ready records has become essential.
              </p>

              <h3 className="service-section-title">Why Outsource Accounting and Bookkeeping Services?</h3>
              <div className="service-features-list">
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Cost efficiency</span>
                </div>
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Access to qualified accounting professionals</span>
                </div>
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Improved financial accuracy and control</span>
                </div>
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Reduced compliance risks</span>
                </div>
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Timely and reliable financial reporting</span>
                </div>
              </div>

              <Accordion type="single" collapsible className="service-accordion">
                <AccordionItem value="accounting">
                  <AccordionTrigger>Accounting Services</AccordionTrigger>
                  <AccordionContent>
                    <ul className="service-detail-list">
                      <li>Design and implementation of accounting systems</li>
                      <li>Accounting software setup and support</li>
                      <li>Maintenance of books of accounts</li>
                      <li>Project and cost accounting</li>
                      <li>Inventory and account reconciliations</li>
                      <li>Petty cash accounting</li>
                      <li>Payroll accounting support</li>
                      <li>VAT and tax-related accounting</li>
                      <li>Fixed asset and depreciation schedules</li>
                      <li>Bank reconciliation statements</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="bookkeeping">
                  <AccordionTrigger>Bookkeeping Services</AccordionTrigger>
                  <AccordionContent>
                    <ul className="service-detail-list">
                      <li>Sales and revenue bookkeeping</li>
                      <li>Purchase and expense bookkeeping</li>
                      <li>Accounts payable management</li>
                      <li>Accounts receivable tracking</li>
                      <li>Cash and bank bookkeeping</li>
                      <li>Fixed assets register maintenance</li>
                      <li>General ledger maintenance</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="reporting">
                  <AccordionTrigger>Financial Reporting and MIS Services</AccordionTrigger>
                  <AccordionContent>
                    <ul className="service-detail-list">
                      <li>Management Information System (MIS) reports</li>
                      <li>Budgeting and forecasting support</li>
                      <li>Inventory and working capital analysis</li>
                      <li>Treasury and cash flow management</li>
                      <li>Sales and revenue analysis</li>
                      <li>Monthly and year-end financial statements</li>
                      <li>Profit and Loss statements</li>
                      <li>Balance Sheets</li>
                      <li>Periodic closing reports</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>

          {/* Taxation Services */}
          <div className="service-main-card">
            <div className="service-main-header">
              <div className="service-main-icon">
                <FileText size={48} />
              </div>
              <div>
                <h2 className="service-main-title">Taxation Services</h2>
                <p className="service-main-subtitle">Comprehensive VAT and Corporate Tax Solutions</p>
              </div>
            </div>
            <img 
              src="https://images.pexels.com/photos/6963857/pexels-photo-6963857.jpeg" 
              alt="Taxation Services"
              className="service-main-image"
            />
            
            {/* VAT Services Section */}
            <div className="service-main-content">
              <h3 className="service-major-heading">VAT Registration, Consulting & Advisory Services</h3>
              <p className="service-intro-text">
                Implementing VAT is not just about understanding the law. It's about applying it correctly across systems, processes, and people without disrupting day-to-day business operations. At Fabsome Consulting, we focus on practical and smooth VAT implementation so businesses remain compliant while continuing to operate efficiently.
              </p>

              <Accordion type="single" collapsible className="service-accordion">
                <AccordionItem value="vat-assessment">
                  <AccordionTrigger>VAT Impact Assessment</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      We assess how VAT affects your business transactions and operations. This includes identifying VAT implications across sales, purchases, supply chains, and internal functions, helping you understand where changes are required and how VAT impacts your overall business model.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="vat-registration">
                  <AccordionTrigger>VAT Registration Support</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      We assist businesses in applying for VAT registration with the Federal Tax Authority, including reviewing eligibility, preparing documentation, and ensuring accurate submission to avoid delays or rejections.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="systems-review">
                  <AccordionTrigger>Systems and IT Review</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      VAT compliance depends heavily on system accuracy. We review your accounting and ERP systems to identify gaps at both master data and transaction levels. Our support includes recommending system changes and assisting with user acceptance testing to ensure your systems are VAT-ready.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="vat-transition">
                  <AccordionTrigger>VAT Transition Management</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      We provide hands-on support during the transition to VAT, guiding businesses through initial implementation, process changes, and compliance requirements to ensure a smooth shift into the VAT regime.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="hypercare">
                  <AccordionTrigger>Post-Implementation Support (Hypercare)</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      Our hypercare support allows businesses to focus on core operations while we handle VAT-related functional aspects. This ensures ongoing compliance, issue resolution, and operational stability after implementation.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="vat-pm">
                  <AccordionTrigger>Dedicated VAT Project Management</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      For implementation engagements, we assign a dedicated project manager who oversees the entire VAT project, coordinates with stakeholders, and ensures timelines and deliverables are met efficiently.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="representation">
                  <AccordionTrigger>Representation before Tax Authorities</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      We assist businesses in identifying matters that require representation before tax authorities and provide support in preparing and submitting applications, responses, and related documentation.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="vat-training">
                  <AccordionTrigger>VAT Training and Knowledge Sessions</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      Correct VAT implementation requires awareness at all levels of the organization. We conduct structured training sessions designed to educate key stakeholders and ensure consistent understanding and application of VAT provisions.
                    </p>
                    <ul className="service-detail-list">
                      <li>Introductory VAT awareness sessions covering fundamental concepts</li>
                      <li>Functional training focused on VAT impact across departments</li>
                      <li>Compliance and reporting training covering VAT returns</li>
                      <li>Awareness sessions for vendors and customers</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="vat-sop">
                  <AccordionTrigger>Development of VAT Standard Operating Procedures (SOPs)</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      VAT is a transaction-based tax, making it critical to align business processes with VAT law. We assist in developing and refining SOPs to embed VAT compliance into daily operations.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="gcc-vat">
                  <AccordionTrigger>GCC VAT Compliance Support</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      We support businesses with VAT compliance across GCC jurisdictions. Our services include preparation, review, and filing of VAT returns, supported by detailed reviews of financial records, invoices, agreements, and VAT workings to ensure accuracy and compliance.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="vat-advisory">
                  <AccordionTrigger>Ongoing VAT Advisory Services</AccordionTrigger>
                  <AccordionContent>
                    <ul className="service-detail-list">
                      <li>Advisory on VAT positions, risks, and documentation requirements</li>
                      <li>Guidance on changes in VAT laws and regulatory updates</li>
                      <li>Assistance with reconsideration requests and challenges to tax authority decisions</li>
                      <li>Support in representation before committees, courts, or authorities</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="clarifications">
                  <AccordionTrigger>Clarifications and Ruling Applications</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      For complex or unclear VAT matters where established positions are not available, we assist with technical research and analysis, preparation and submission of clarification or ruling requests to tax authorities, and advisory support based on regulatory guidance.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="vat-audit">
                  <AccordionTrigger>VAT Audit Support</AccordionTrigger>
                  <AccordionContent>
                    <ul className="service-detail-list">
                      <li>Pre-audit readiness reviews</li>
                      <li>Support during tax audits with documentation and transaction analysis</li>
                      <li>Identification of risks and exposure areas</li>
                      <li>Advisory on corrective actions and post-audit compliance</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="vat-refund">
                  <AccordionTrigger>VAT Refund Assistance</AccordionTrigger>
                  <AccordionContent>
                    <ul className="service-detail-list">
                      <li>Advising whether to claim a refund or carry forward excess VAT</li>
                      <li>Preparing and submitting refund applications</li>
                      <li>Responding to tax authority queries during the refund process</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="vat-healthcheck">
                  <AccordionTrigger>VAT Health Check and Review</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      Regular VAT reviews help identify compliance gaps and reduce future risks. We conduct comprehensive VAT reviews including review of business transactions, VAT policies, process reviews, and identification of gaps and corrective actions.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="vat-outsourcing">
                  <AccordionTrigger>VAT Function Outsourcing and Deputation</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      Managing VAT compliance requires specialized knowledge and experience. We provide VAT professionals through outsourcing, co-sourcing, or deputation models, allowing businesses to focus on growth while compliance is managed by experts.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Corporate Tax Services Section */}
              <h3 className="service-major-heading" style={{marginTop: '80px'}}>UAE Corporate Tax Implementation & Advisory Services</h3>
              <p className="service-intro-text">
                The introduction of Corporate Tax in the UAE marks a major shift in the country's tax landscape. With Corporate Tax applying to financial years starting on or after 1 June 2023, careful planning and structured implementation are essential. At Fabsome Consulting, we help businesses understand the impact of Corporate Tax and implement it in a way that ensures compliance while allowing business operations to continue smoothly.
              </p>

              <Accordion type="single" collapsible className="service-accordion">
                <AccordionItem value="ct-assessment">
                  <AccordionTrigger>Corporate Tax Impact Assessment</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      We conduct an initial assessment to determine the applicability of the UAE Corporate Tax regime to businesses, including identifying in-scope entities, exempt persons, and potential exclusions under the law.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ct-detailed">
                  <AccordionTrigger>Detailed Corporate Tax Assessment</AccordionTrigger>
                  <AccordionContent>
                    <ul className="service-detail-list">
                      <li>Evaluation of business activities and income streams</li>
                      <li>Review of inter-company transactions and pricing policies</li>
                      <li>Identification of potential tax risks, uncertainties, and exposure areas</li>
                      <li>Assessment of Transfer Pricing implications under UAE CT law</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ct-implementation">
                  <AccordionTrigger>Corporate Tax Implementation Support</AccordionTrigger>
                  <AccordionContent>
                    <ul className="service-detail-list">
                      <li>Assistance in designing and implementing Corporate Tax and Transfer Pricing policies</li>
                      <li>Advisory on structural or operational changes required</li>
                      <li>Evaluation of accounting and system readiness for Corporate Tax compliance</li>
                      <li>Assistance with Corporate Tax registration, including tax group registrations</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ct-compliance">
                  <AccordionTrigger>UAE Corporate Tax Compliance Services</AccordionTrigger>
                  <AccordionContent>
                    <ul className="service-detail-list">
                      <li>Preparation of Corporate Tax returns</li>
                      <li>Electronic filing of Corporate Tax returns</li>
                      <li>Assistance in preparation of Transfer Pricing documentation</li>
                      <li>Evaluation of arm's length pricing for related party transactions</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ct-training">
                  <AccordionTrigger>Corporate Tax Training and Awareness</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      We conduct technical and functional training sessions tailored to different stakeholders, ensuring key personnel understand their roles and responsibilities under the Corporate Tax regime.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ct-advisory">
                  <AccordionTrigger>Corporate Tax Advisory Services</AccordionTrigger>
                  <AccordionContent>
                    <ul className="service-detail-list">
                      <li>Updates on developments in UAE Corporate Tax framework</li>
                      <li>Advisory on tax positions, policies, and compliance strategies</li>
                      <li>Guidance on structuring inter-company transactions</li>
                      <li>High-level advisory on tax efficiency while maintaining compliance</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>

          {/* ICV Advisory Services */}
          <div className="service-main-card">
            <div className="service-main-header">
              <div className="service-main-icon">
                <TrendingUp size={48} />
              </div>
              <div>
                <h2 className="service-main-title">ICV Advisory Services</h2>
                <p className="service-main-subtitle">How to Get an ICV Certificate in the UAE</p>
              </div>
            </div>
            <img 
              src="https://images.pexels.com/photos/2977565/pexels-photo-2977565.jpeg" 
              alt="ICV Advisory Services"
              className="service-main-image"
            />
            <div className="service-main-content">
              <p className="service-intro-text">
                In the UAE, the In-Country Value (ICV) program plays a key role in encouraging businesses to contribute to the local economy. An ICV Certificate is often a mandatory requirement for suppliers and contractors looking to work with government entities and semi-government organizations.
              </p>

              <div className="service-notice">
                <h4>📢 Important Update (Effective 1 January 2025)</h4>
                <p>
                  For financial years ending after 31 December 2024, ICV certificates must be supported by stand-alone audited financial statements of the specific legal entity being certified. Management accounts derived from consolidated financial statements will no longer be accepted for ICV purposes.
                </p>
              </div>

              <h3 className="service-section-title">What Is ICV Certification?</h3>
              <p className="service-text">
                ICV stands for In-Country Value. The ICV program is a national initiative designed to measure and enhance the contribution of businesses to the UAE economy. It evaluates factors such as local spending, Emiratization, and capital investment within the country.
              </p>

              <h3 className="service-section-title">Why ICV Certification Is Important</h3>
              <div className="service-features-list">
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Eligibility for government and semi-government contracts</span>
                </div>
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Improved competitiveness in tenders</span>
                </div>
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Demonstration of commitment to local economic development</span>
                </div>
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Enhanced transparency in procurement</span>
                </div>
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Long-term sustainability through local investment</span>
                </div>
              </div>

              <h3 className="service-section-title">Key Requirements for ICV Certification</h3>
              <ul className="service-detail-list">
                <li>Expenditure on local suppliers and service providers</li>
                <li>Workforce composition, including Emirati employees</li>
                <li>Capital investment within the UAE</li>
                <li>Audited financial statements for the relevant financial year</li>
                <li>Supporting documentation such as purchase records and payroll data</li>
              </ul>

              <h3 className="service-section-title">Step-by-Step Process to Obtain an ICV Certificate</h3>
              <Accordion type="single" collapsible className="service-accordion">
                <AccordionItem value="eligibility">
                  <AccordionTrigger>1. Entity Eligibility Review</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      Confirm that the legal entity applying for ICV certification is correctly registered in the UAE and has eligible financial statements for the assessment period.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="preparation">
                  <AccordionTrigger>2. Preparation of Financial Information</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      Prepare audited stand-alone financial statements and supporting schedules required for ICV computation, ensuring alignment with the latest ICV guidelines.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="data-collection">
                  <AccordionTrigger>3. Data Collection and Validation</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      Compile detailed data relating to local procurement, employee costs, capital investments, and revenue breakdowns. Accuracy at this stage is critical.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="calculation">
                  <AccordionTrigger>4. ICV Score Calculation</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      The ICV score is calculated based on the approved methodology, taking into account local spend, Emiratization, and investment metrics.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="certification">
                  <AccordionTrigger>5. Certification by an Approved Certifying Body</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      The prepared ICV template and supporting documents are reviewed and certified by an approved ICV certifying body.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="renewal">
                  <AccordionTrigger>6. Validity and Renewal</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      ICV Certificates are typically valid for a limited period and must be renewed annually using updated financial information.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <h3 className="service-section-title">How Fabsome Consulting Supports Your ICV Journey</h3>
              <p className="service-text">
                While we do not issue ICV Certificates, we work closely with clients to ensure they are fully prepared for certification and positioned to achieve an optimal ICV score.
              </p>
              <div className="service-features-list">
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>ICV readiness assessment</span>
                </div>
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Assistance in data collection and validation</span>
                </div>
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Advisory on ICV score optimization strategies</span>
                </div>
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Preparation and review of ICV templates</span>
                </div>
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Coordination support with approved ICV certifying bodies</span>
                </div>
                <div className="feature-item-inline">
                  <CheckCircle2 size={20} />
                  <span>Ongoing advisory to improve future ICV performance</span>
                </div>
              </div>
            </div>
          </div>

          {/* E-Invoicing Advisory */}
          <div className="service-main-card">
            <div className="service-main-header">
              <div className="service-main-icon">
                <Smartphone size={48} />
              </div>
              <div>
                <h2 className="service-main-title">E-Invoicing Advisory</h2>
                <p className="service-main-subtitle">Tax Technology & E-Invoicing Advisory Services</p>
              </div>
            </div>
            <img 
              src="https://images.pexels.com/photos/6964340/pexels-photo-6964340.jpeg" 
              alt="E-Invoicing Advisory"
              className="service-main-image"
            />
            <div className="service-main-content">
              <p className="service-intro-text">
                At Fabsome Consulting, our tax technology and e-invoicing advisory team supports businesses across the UAE and the wider GCC in navigating regulatory changes through practical, technology-enabled solutions. Our focus is on helping organisations implement compliant, efficient, and scalable tax and e-invoicing frameworks that align with their operational needs.
              </p>

              <h3 className="service-section-title">What We Do</h3>
              <p className="service-text">
                Through in-house expertise and strategic technology partnerships, we provide a wide range of tax technology and e-invoicing related services:
              </p>

              <Accordion type="single" collapsible className="service-accordion">
                <AccordionItem value="readiness">
                  <AccordionTrigger>Tax and Regulatory Change Readiness</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      We assist businesses in preparing for new and evolving tax and regulatory requirements, including e-invoicing mandates, by assessing readiness and recommending practical implementation roadmaps.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="vendor">
                  <AccordionTrigger>Vendor Evaluation and Selection</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      We support clients in evaluating and selecting appropriate technology vendors based on business size, industry requirements, compliance needs, and system compatibility.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="implementation">
                  <AccordionTrigger>Technology Implementation and Project Support</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      We provide end-to-end support for implementation of tax and e-invoicing solutions, including project coordination, governance support, and stakeholder alignment to ensure smooth and timely delivery.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="analytics">
                  <AccordionTrigger>Tax Function Analytics and Control Frameworks</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      We assist in designing and implementing analytics and control frameworks that enhance visibility, accuracy, and control over tax data and compliance processes.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="custom">
                  <AccordionTrigger>Customised Solutions and Integrations</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      Where standard solutions are insufficient, we support the development of customised solutions, including system integrations, reporting tools, workflow automation, and data analytics tailored to business requirements.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="training">
                  <AccordionTrigger>Analytics and AI Awareness Training</AccordionTrigger>
                  <AccordionContent>
                    <p className="service-text">
                      We conduct practical training sessions to help finance and tax teams understand the use of analytics, automation, and AI concepts within tax and compliance functions.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <h3 className="service-section-title">Our Team and Collaboration Model</h3>
              <p className="service-text">
                Our tax technology advisory team comprises professionals with strong backgrounds in accounting, taxation, and technology-enabled transformation. The team combines practical tax experience with technical skills in areas such as data analysis, system integration, process automation, and solution architecture.
              </p>
              <p className="service-text">
                We collaborate with specialist consultants and software vendors where required and work closely with domain experts across corporate tax, VAT, transfer pricing, e-invoicing, and regulatory compliance. This collaborative approach allows us to deliver solutions that are technically sound, compliant, and aligned with business realities.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <div className="services-cta-content">
          <h2 className="services-cta-title">Need Expert Advice?</h2>
          <p className="services-cta-description">
            Let's discuss how our services can help your business achieve compliance and growth
          </p>
          <Link to="/contact" className="btn-primary btn-large">
            Contact Our Team
            <ArrowRight size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesDetailed;